import { css } from '@emotion/react';
import { useContext, useState } from 'react';
import t from 'react-translate';
import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { deleteMentorshipProgramGuide } from 'redux/actions/mentoring-program-guides';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import MoreOptionsTarget from 'athena/components/more-options-target';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import NvDropdown, { NvDropdownAlign, NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import { CellProps } from '.';
import { config } from '@config/pendo.config.json';


const OptionsCell = ({
  className,
  cell,
}: CellProps) => {
  const translationBase = t.MENTORING_PROGRAMS.GUIDANCE.GUIDES;

  const styles = css`
    .options-target {
      opacity: 0;
      flex-shrink: 0;
    }

    .options-dropdown:focus {
      .options-target {
        opacity: 1;
      }
    }
  `;

  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { getContext } = cell;
  const {
    table: {
      options: { meta },
    },
  } = getContext();
  const { setShowCreateGuideFlyout, setGuideFlyoutDefaultValues } = meta;
  const guide = cell.getValue();

  const options: NvDropdownOption[] = [
    {
      type: 'text',
      text: translationBase.OPTIONS.VIEW(),
      callback: () => viewGuide(),
      dataQa: config.pendo.athena.mentorshipProgram.guides.optionsMenuDropdown.view,
    },
    {
      type: 'text',
      text: translationBase.OPTIONS.EDIT(),
      callback: () => editGuide(),
      dataQa: config.pendo.athena.mentorshipProgram.guides.optionsMenuDropdown.edit,
    },
    {
      type: 'divider',
    },
    {
      type: 'text',
      text: translationBase.OPTIONS.MAKE_A_COPY(),
      callback: () => copyGuide(),
      dataQa: config.pendo.athena.mentorshipProgram.guides.optionsMenuDropdown.copy,
    },
    {
      type: 'text',
      text: translationBase.OPTIONS.DELETE(),
      textClassName: 'color-danger',
      callback: () => deleteGuide(),
      dataQa: config.pendo.athena.mentorshipProgram.guides.optionsMenuDropdown.delete,
    },
  ];

  const viewGuide = () => {
    const params = { guideId: guide.id };
    $state.go($state.current, params, { notify: false });
  };

  const editGuide = () => {
    setGuideFlyoutDefaultValues(guide);
    setShowCreateGuideFlyout(true);
  };

  const copyGuide = () => {
    const { id, ...rest } = guide;
    setGuideFlyoutDefaultValues(rest);
    setShowCreateGuideFlyout(true);
  };

  const deleteGuide = () => {
    dispatch(openConfirmationDialog({
      title: translationBase.OPTIONS.DELETE_GUIDE_DIALOG.TITLE(),
      bodyText: translationBase.OPTIONS.DELETE_GUIDE_DIALOG.BODY(),
      confirmText: t.FORM.DELETE(),
      onConfirm: () => {
        wrapThunkAction(dispatch(deleteMentorshipProgramGuide({
          mentoringProgramId: mentoringProgram.id,
          guideId: guide.id,
        }))).then(() => {
          dispatch(addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.FORM.SUCCESS_BANG(),
            message: translationBase.OPTIONS.GUIDE_DELETED_SUCCESS(),
          }));
        });
      },
    }));
    setIsDropdownOpen(false);
  };

  return (
    <div
      css={styles}
      className={`d-flex align-items-center justify-content-end ${className}`}
      onClick={(e) => e.stopPropagation()}
      // ^ Prevent row click event from firing when clicking on options cell
    >
      <NvDropdown
        items={options}
        align={NvDropdownAlign.RIGHT}
        buttonStyle={NvDropdownButtonStyle.CUSTOM}
        customTargetClass='options-dropdown justify-content-end'
        customTarget={() => (
          <MoreOptionsTarget className='options-target' />
        )}
        toggleDataQa={config.pendo.athena.mentorshipProgram.guides.optionsMenuDropdown.toggle}
        onToggle={setIsDropdownOpen}
        ariaExpanded={isDropdownOpen}
        ariaRole='menu'
        // altLabel={}
      />
    </div>
  );
};

export default OptionsCell;
